const DEFAULT_MAX_STRING_LENGTH = 240;
const DEFAULT_HEAD_LENGTH = 150;
const DEFAULT_TAIL_LENGTH = 30;

function truncateString(value, maxLength = DEFAULT_MAX_STRING_LENGTH) {
  if (typeof value !== 'string') return value;
  if (value.length <= maxLength) return value;

  const headLength = Math.min(DEFAULT_HEAD_LENGTH, maxLength);
  const tailLength = Math.min(DEFAULT_TAIL_LENGTH, Math.max(maxLength - headLength, 0));

  const head = value.slice(0, headLength);
  const tail = tailLength > 0 ? value.slice(value.length - tailLength) : '';
  const omitted = Math.max(value.length - (head.length + tail.length), 0);

  if (tail) {
    return `${head}...${tail} [truncated ${omitted} chars]`;
  }
  return `${head}... [truncated ${omitted} chars]`;
}

function sanitizeStrings(value, maxLength) {
  if (typeof value === 'string') {
    return truncateString(value, maxLength);
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeStrings(item, maxLength));
  }

  if (value && typeof value === 'object') {
    const result = {};
    for (const [key, val] of Object.entries(value)) {
      result[key] = sanitizeStrings(val, maxLength);
    }
    return result;
  }

  return value;
}

export function buildMonitorJson(raw, variant) {
  if (!raw) {
    return '';
  }

  // Back-compat: older logs may have been stored as { request, response }.
  // Only show the response in the UI.
  const rawToShow =
    raw && typeof raw === 'object' && 'response' in raw && 'request' in raw
      ? raw.response
      : raw;

  // For Image Engine, sanitize long strings to avoid oversized payloads in the UI.
  if (variant === 'imagex') {
    const sanitized = sanitizeStrings(rawToShow, DEFAULT_MAX_STRING_LENGTH);
    return JSON.stringify(sanitized, null, 2);
  }

  // For Structured Output, if the upstream returned a choices[0].message.content
  // containing JSON (as a string), render that parsed JSON for clarity instead
  // of the full OpenAI-style wrapper.
  if (variant === 'structured') {
    try {
      const content = Array.isArray(rawToShow?.choices)
        ? rawToShow.choices[0]?.message?.content
        : undefined;
      if (typeof content === 'string') {
        try {
          const parsedContent = JSON.parse(content);
          return JSON.stringify(parsedContent, null, 2);
        } catch {
          // content was not valid JSON, fall back to showing full wrapper
        }
      }
    } catch {
      // ignore and fall through
    }
  }

  return JSON.stringify(rawToShow, null, 2);
}

function asPreviewSource(candidate) {
  if (!candidate || typeof candidate !== 'object') return null;

  if (typeof candidate.url === 'string' && candidate.url) {
    return candidate.url;
  }

  if (typeof candidate.b64_json === 'string' && candidate.b64_json) {
    return `data:image/png;base64,${candidate.b64_json}`;
  }

  if (typeof candidate.b64Json === 'string' && candidate.b64Json) {
    return `data:image/png;base64,${candidate.b64Json}`;
  }

  return null;
}

export function extractImagePreview(raw) {
  if (!raw) return null;

  // Back-compat: if stored as { request, response }, look in response only.
  const source =
    raw && typeof raw === 'object' && 'response' in raw && 'request' in raw
      ? raw.response
      : raw;

  const direct = asPreviewSource(source);
  if (direct) return direct;

  const collections = [source.images, source.data, source.results];
  for (const collection of collections) {
    if (!Array.isArray(collection)) continue;
    for (const item of collection) {
      const src = asPreviewSource(item);
      if (src) return src;
    }
  }

  return null;
}
